import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';

import type {
  MatchdayPicksTabQuery,
  MatchdayPicksTabQueryVariables,
} from './__generated__/useMatchdayPicksTab.graphql.ts';

const MATCHDAY_PICKS_TAB_QUERY = gql`
  query MatchdayPicksTabQuery {
    market {
      id
      activeSteppedDynamicCampaigns {
        nodes {
          id
          player {
            slug
            sport
          }
        }
      }
    }
  }
` as TypedDocumentNode<MatchdayPicksTabQuery, MatchdayPicksTabQueryVariables>;

// `activeSteppedDynamicCampaigns` takes no sport argument and returns every sport's campaigns, so
// the player's own sport is what tells them apart. The field is typed `Player` — football's type —
// but it resolves for every sport, and `sport` comes from `AnyPlayerInterface`.
//
// Gated on the campaign alone, never on `primaryOffer`: that field is null for a campaign that has
// nothing on sale and has never sold, which is a live sale with no buyable pick yet — the entry
// point should still be there.
export const isLiveMatchdayPickFor =
  (sport: Sport) =>
  (campaign: { player?: { sport: Sport } | null }): boolean =>
    campaign.player?.sport === sport;

export const useMatchdayPicksTab = ({ sport }: { sport: Sport }) => {
  const { data } = useQuery(MATCHDAY_PICKS_TAB_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const campaigns = useMemo(
    () =>
      (data?.market?.activeSteppedDynamicCampaigns.nodes || []).filter(
        isLiveMatchdayPickFor(sport)
      ),
    [data?.market?.activeSteppedDynamicCampaigns.nodes, sport]
  );

  return {
    show: campaigns.length > 0,
  };
};
